import { useEffect, useRef, useState } from 'react'
import {
	type LoaderFunctionArgs,
	type MetaFunction,
	NavLink,
	type ShouldRevalidateFunctionArgs,
	useFetcher,
	useLoaderData,
	useLocation,
} from 'react-router'
import LazyLoad, { type ILazyLoadInstance } from 'vanilla-lazyload'
import { TilComponent } from './til-component'
import { H1 } from '~/components/typography'
import { useFooterObserver } from '~/hooks/use-footer-observer'
import { type TilMdxPage } from '~/schemas/github'
import { getPaginatedTilList } from '~/utils/mdx-utils.server'
import { getTilLoaderData } from '~/utils/route-loader-helpers.server'
import {
	getInitialDocumentUrl,
	getTilDocumentUrl,
	shouldUseTilScrollFallback,
} from '~/utils/til-url'

// css
import '~/styles/til.css'

export function shouldRevalidate({
	currentUrl,
	nextUrl,
	defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) {
	if (currentUrl.pathname === nextUrl.pathname) {
		return false
	}

	return defaultShouldRevalidate
}

export const meta: MetaFunction<typeof loader> = () => {
	return [
		{ title: `Taran "tearing it up" Bains | Today I Learned` },
		{
			name: 'description',
			content: 'A list of things I have learned today',
		},
	]
}

export async function loader({ url }: LoaderFunctionArgs) {
	return getTilLoaderData({
		requestUrl: url.href,
		getPaginatedTilList,
	})
}

export default function TilPage() {
	const { fullList, maxOffset, serverEndOffset } =
		useLoaderData<typeof loader>()
	const fetcher = useFetcher<typeof loader>()
	const location = useLocation()
	const lazyLoadRef = useRef<ILazyLoadInstance | null>(null)
	const hasUsedTilScrollFallbackRef = useRef(false)

	// TODO: migrate to useReducer
	const [items, setItems] = useState<TilMdxPage[]>(fullList)
	const [offset, setOffset] = useState<number>(serverEndOffset)
	const [lastProcessedData, setLastProcessedData] = useState<
		typeof fetcher.data | null
	>(null)

	// Handle fetcher data during render
	if (
		fetcher.data &&
		fetcher.state !== 'loading' &&
		fetcher.data !== lastProcessedData
	) {
		setLastProcessedData(fetcher.data)
		setItems((prevItems) => [...prevItems, ...(fetcher?.data?.fullList ?? [])])
	}

	useFooterObserver({
		onIntersect: () => {
			if (offset === maxOffset) return
			fetcher
				.load(`/til?fromFetcher=true&offset=${offset + 1}`)
				.then(() => {
					setOffset((p) => p + 1)
				})
				.catch((err) => {
					throw err
				})
		},
	})

	useEffect(() => {
		if (lazyLoadRef.current === null) {
			lazyLoadRef.current = new LazyLoad()
		} else {
			lazyLoadRef.current.update()
		}
	}, [items])

	useEffect(() => {
		const targetId = new URLSearchParams(location.search).get('til')

		if (
			hasUsedTilScrollFallbackRef.current ||
			!targetId ||
			!shouldUseTilScrollFallback({
				initialDocumentUrl: getInitialDocumentUrl(),
				currentDocumentUrl: getTilDocumentUrl({
					pathname: location.pathname,
					search: location.search,
				}),
				hash: location.hash,
				targetId,
			})
		) {
			return
		}

		const animationFrameId = requestAnimationFrame(() => {
			const target = document.getElementById(targetId)

			if (!target) return

			target.scrollIntoView()
			hasUsedTilScrollFallbackRef.current = true
		})

		return () => cancelAnimationFrame(animationFrameId)
	}, [location.hash, location.pathname, location.search])

	return (
		<main className="mx-auto w-full max-w-screen-xl px-4 md:px-20">
			<div className="mt-6 mb-4 flex items-center md:mt-14 md:mb-10">
				<H1 className="">Today I Learned</H1>
				<NavLink
					target="_blank"
					prefetch="intent"
					to="/til/rss.xml"
					className="group ml-4 no-underline transition-transform duration-200 hover:scale-110"
				>
					<span className="sr-only">Get the RSS feed for Today I Learned</span>

					<svg
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 24 24"
						strokeWidth={1.5}
						stroke="currentColor"
						className="size-6 md:size-10"
						aria-hidden="true"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							d="M12.75 19.5v-.75a7.5 7.5 0 0 0-7.5-7.5H4.5m0-6.75h.75c7.87 0 14.25 6.38 14.25 14.25v.75M6 18.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z"
						/>
					</svg>
				</NavLink>
			</div>
			<div className='after:bg-dark-gray-100 relative pb-8 after:absolute after:top-5 after:left-0 after:hidden after:h-[calc(100%_-_20px)] after:w-0.5 after:content-[""] md:pl-24 after:md:block lg:px-26 xl:min-w-[1200px] dark:after:bg-white'>
				<div className="prose prose-light dark:prose-dark max-w-full">
					{items?.map((til) => {
						return (
							<TilComponent
								key={`${til.frontmatter.title}-${til.frontmatter.date}`}
								til={til}
							/>
						)
					})}
				</div>
			</div>
		</main>
	)
}
